import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Mic,
  Square,
  Play,
  Pause,
  RotateCcw,
  Trash2,
  Timer as TimerIcon,
  Download,
} from "lucide-react";
import { BASE_URL } from "../../../config";

/**
 * Answer shape we emit via onAnswerChange:
 * {
 *   recording: {
 *     url: string,
 *     mimeType: string,
 *     size: number,
 *     durationMs: number,
 *     mediaId?: string
 *   },
 *   transcript?: {
 *     text: string,
 *     lang?: string,
 *     confidence?: number
 *   }
 * }
 */
export default function SpeakingRenderer({
  item,
  onAnswerChange,
  initialAnswers,
  questionNumber,
  hideStem = false,
  uploadOnStop = true,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");

  const timings = item?.body?.timings || {};
  const capture = item?.body?.capture || {};
  const layout = item?.body?.layout || "top-bottom";

  const prepTimeSec = Number(timings.prepTimeSec) || 0;
  const responseTimeSec = Number(timings.responseTimeSec) || 60;
  const minSec = Number(capture.minSec) || 0;
  const maxSec = Number(capture.maxSec) || responseTimeSec;

  const initRec = useMemo(() => {
    if (!initialAnswers) return null;
    if (initialAnswers.recording) return initialAnswers.recording;
    const { url, mimeType, size, durationMs } = initialAnswers || {};
    if (url) return { url, mimeType, size, durationMs };
    return null;
  }, [initialAnswers]);

  // ✅ اگر از قبل transcript داشتیم هم ریکاور کن
  const initTranscript = useMemo(() => {
    const tr = initialAnswers?.transcript;
    if (tr?.text) return tr;
    return null;
  }, [initialAnswers]);

  const [phase, setPhase] = useState(initRec ? "review" : "idle");
  const [prepLeft, setPrepLeft] = useState(prepTimeSec);
  const [recLeft, setRecLeft] = useState(responseTimeSec);
  const [elapsed, setElapsed] = useState(0);
  const [recording, setRecording] = useState(initRec);
  const [transcript, setTranscript] = useState(initTranscript); // ✅ جدید
  const [playState, setPlayState] = useState("paused");
  const [err, setErr] = useState("");

  const mediaStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);
  const tickIvRef = useRef(null);
  const prepIvRef = useRef(null);

  const mmss = (sec) => {
    const s = Math.max(0, Math.floor(sec));
    const m = Math.floor(s / 60).toString().padStart(2, "0");
    const r = (s % 60).toString().padStart(2, "0");
    return `${m}:${r}`;
  };

  useEffect(() => {
    return () => {
      stopAll(true);
      if (recording?.url?.startsWith("blob:")) URL.revokeObjectURL(recording.url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setRecording(initRec);
    setTranscript(initTranscript);
    setPhase(initRec ? "review" : "idle");
  }, [initRec, initTranscript]);

  const pickMimeType = () => {
    const candidates = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/ogg;codecs=opus",
      "audio/ogg",
      "audio/mp4",
    ];
    for (const mt of candidates) {
      if (window.MediaRecorder && MediaRecorder.isTypeSupported?.(mt)) return mt;
    }
    return "";
  };

  // ✅ کمک‌تابع: تبدیل صوت به متن از بک‌اند
  const transcribeUploadedAudio = async ({ mediaUrl, mediaId, token }) => {
    // اگر شما endpoint دیگری دارید اینجا عوضش کن
    const endpoint = `${BASE_URL || ""}/ai/transcribe`;

    const res = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { "x-access-token": token } : {}),
      },
      body: JSON.stringify({
        mediaUrl,   // یا mediaId
        mediaId,
        lang: i18n.language, // fa/en...
        kind: "speaking",
        itemId: item?._id,
      }),
    });

    if (!res.ok) {
      const tx = await res.text().catch(() => "");
      throw new Error(tx || "TRANSCRIBE_FAILED");
    }

    // انتظار: { ok:true, data:{ text, lang, confidence } }
    const json = await res.json();
    const tr = json?.data || json?.transcript || null;
    if (!tr?.text) return null;

    return {
      text: String(tr.text),
      lang: tr.lang,
      confidence: typeof tr.confidence === "number" ? tr.confidence : undefined,
    };
  };

  const startPrep = async () => {
    setErr("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
    } catch (e) {
      setErr(
        t("speaking.permission_denied", "Microphone permission denied or unavailable.")
      );
      return;
    }

    if (prepTimeSec > 0) {
      setPrepLeft(prepTimeSec);
      setPhase("prep");
      prepIvRef.current = setInterval(() => {
        setPrepLeft((p) => {
          if (p <= 1) {
            clearInterval(prepIvRef.current);
            beginRecording();
            return 0;
          }
          return p - 1;
        });
      }, 1000);
    } else {
      beginRecording();
    }
  };

  const beginRecording = () => {
    setErr("");
    setPhase("recording");
    setRecLeft(responseTimeSec);
    setElapsed(0);
    chunksRef.current = [];
    setTranscript(null); // ✅ هر بار ضبط جدید، متن قبلی پاک شود

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const token = user?.token;

    try {
      const mimeType = pickMimeType();
      const mr = new MediaRecorder(
        mediaStreamRef.current,
        mimeType ? { mimeType } : {}
      );
      mediaRecorderRef.current = mr;

      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };

      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, {
          type: mr.mimeType || "audio/webm",
        });
        const localUrl = URL.createObjectURL(blob);

        let meta = {
          url: localUrl,
          mimeType: blob.type,
          size: blob.size,
          durationMs: elapsed,
        };

        let tr = null;

        // ✅ آپلود + سپس STT
        if (uploadOnStop) {
          try {
            const file = new File(
              [blob],
              `speaking-${item?._id || "q"}-${Date.now()}.webm`,
              { type: blob.type || "audio/webm" }
            );

            const fd = new FormData();
            fd.append("file", file);
            fd.append("name", file.name);

            const headers = token ? { "x-access-token": token } : undefined;

            const res = await fetch(`${BASE_URL || ""}/media`, {
              method: "POST",
              headers,
              body: fd,
            });

            if (res.ok) {
              const json = await res.json();
              const mediaDoc = json?.data;

              if (mediaDoc?.url) {
                if (meta.url?.startsWith("blob:")) URL.revokeObjectURL(meta.url);

                meta = {
                  ...meta,
                  url: mediaDoc.url,       // ✅ لینک سرور
                  mediaId: mediaDoc._id,   // ✅ رفرنس
                  serverMimeType: mediaDoc.mimeType,
                };

                // ✅ اینجا تبدیل به متن
                try {
                  tr = await transcribeUploadedAudio({
                    mediaUrl: mediaDoc.url,
                    mediaId: mediaDoc._id,
                    token,
                  });
                } catch (e) {
                  // اگر STT شکست خورد، رکوردینگ رو نگه می‌داریم فقط متن خالی می‌مونه
                  // می‌تونی toast هم بزنی اگر خواستی
                  // setErr(t("speaking.transcribe_failed", "Transcription failed"));
                }
              }
            }
          } catch (e) {
            // آپلود شکست خورد → فقط blob url
          }
        }

        setRecording(meta);
        setTranscript(tr);
        setPhase("review");

        if (typeof onAnswerChange === "function") {
          // ✅ هم لینک + هم متن را با هم ذخیره کن
          onAnswerChange({
            recording: meta,
            transcript: tr || undefined,
          });
        }

        stopStreamOnly();
      };

      mr.start(100);

      const t0 = performance.now();
      tickIvRef.current = setInterval(() => {
        const now = performance.now();
        const newElapsed = now - t0;
        setElapsed(newElapsed);

        setRecLeft(() => {
          const secLeft = Math.max(
            0,
            Math.ceil(responseTimeSec - newElapsed / 1000)
          );
          if (secLeft <= 0) {
            stopRecording();
            return 0;
          }
          if (maxSec && newElapsed / 1000 >= maxSec) {
            stopRecording();
            return 0;
          }
          return secLeft;
        });
      }, 200);
    } catch (e) {
      setErr(t("speaking.recorder_error", "Could not start recorder in this browser."));
      stopAll(true);
    }
  };

  const stopRecording = () => {
    try {
      if (tickIvRef.current) {
        clearInterval(tickIvRef.current);
        tickIvRef.current = null;
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
    } catch (_) {}
  };

  const stopStreamOnly = () => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((tr) => tr.stop());
      mediaStreamRef.current = null;
    }
  };

  const stopAll = (revoke = false) => {
    if (prepIvRef.current) {
      clearInterval(prepIvRef.current);
      prepIvRef.current = null;
    }
    if (tickIvRef.current) {
      clearInterval(tickIvRef.current);
      tickIvRef.current = null;
    }
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
    } catch (_) {}
    stopStreamOnly();

    if (revoke && recording?.url?.startsWith("blob:")) URL.revokeObjectURL(recording.url);
  };

  const play = () => {
    const el = audioRef.current;
    if (!el) return;
    el.play().then(() => setPlayState("playing")).catch(() => {});
  };

  const pause = () => {
    const el = audioRef.current;
    if (!el) return;
    el.pause();
    setPlayState("paused");
  };

  const onAudioEnded = () => setPlayState("paused");

  const removeRecording = () => {
    if (recording?.url?.startsWith("blob:")) URL.revokeObjectURL(recording.url);
    setRecording(null);
    setTranscript(null);
    setPhase("idle");
    setElapsed(0);
    setRecLeft(responseTimeSec);
    if (typeof onAnswerChange === "function") onAnswerChange(null);
  };

  const tooShort = useMemo(
    () => elapsed / 1000 < minSec && phase === "recording",
    [elapsed, minSec, phase]
  );

  const Stem = () => (
    <div
      dir={isRtl ? "rtl" : "ltr"}
      className="prose max-w-none text-gray-800 mb-3 text-center"
      dangerouslySetInnerHTML={{
        __html: item?.stem?.fa || item?.stem?.en || "",
      }}
    />
  );

  const Timer = ({ icon = false, valueSec, label }) => (
    <div className="inline-flex items-center gap-2 rounded-md bg-gray-100 px-3 py-1 text-sm">
      {icon && <TimerIcon className="w-4 h-4" />}
      <span className="font-mono">{mmss(valueSec)}</span>
      {label ? <span className="text-gray-500">{label}</span> : null}
    </div>
  );

  return (
    <div className="rounded-lg bg-white p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs text-gray-500">
          {t("preview.question_n", "Question")} {questionNumber}
        </div>
        <div className="flex items-center gap-2">
          {phase === "prep" && (
            <Timer icon valueSec={prepLeft} label={t("speaking.prep", "prep")} />
          )}
          {phase === "recording" && (
            <Timer
              icon
              valueSec={Math.max(0, Math.ceil(recLeft))}
              label={t("speaking.time_left", "left")}
            />
          )}
        </div>
      </div>

      <div
        className={
          layout === "left-right"
            ? "grid grid-cols-1 md:grid-cols-2 gap-4"
            : "space-y-4"
        }
      >
        <div>
          <Stem />
        </div>

        <div className="space-y-3">
          {phase === "idle" && (
            <div className="flex flex-col items-center gap-2 text-center">
              <button
                onClick={startPrep}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
              >
                <Mic className="w-4 h-4" />
                {t("speaking.start_recording", "Start recording")}
              </button>
            </div>
          )}

          {phase === "prep" && (
            <div className="flex items-center gap-2 justify-center text-center">
              <div className="text-sm text-gray-700">
                {t("speaking.get_ready", "Get ready...")}
              </div>
              <Timer icon valueSec={prepLeft} />
            </div>
          )}

          {phase === "recording" && (
            <div className="flex flex-col items-center gap-3 text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-rose-100 text-rose-700">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-500"></span>
                </span>
                {t("speaking.recording", "Recording")}
              </div>

              <div className="flex items-center gap-3">
                <Timer valueSec={Math.ceil(elapsed / 1000)} label={t("speaking.elapsed", "elapsed")} />
                <Timer valueSec={Math.max(0, Math.ceil(recLeft))} label={t("speaking.left", "left")} />
              </div>

              <button
                onClick={stopRecording}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-rose-600 text-white hover:bg-rose-700"
              >
                <Square className="w-4 h-4" />
                {t("speaking.stop", "Stop")}
              </button>

              {minSec > 0 && (
                <div className={`text-xs ${tooShort ? "text-amber-600" : "text-gray-500"}`}>
                  {t("speaking.min_hint", {
                    defaultValue: "You must speak at least {{n}} seconds.",
                    n: minSec,
                  })}
                </div>
              )}
            </div>
          )}

          {phase === "review" && recording && (
            <div className="space-y-2 text-center">
              <audio
                ref={audioRef}
                // ✅ اگر recording.url از سرور برمی‌گرده ممکنه absolute/relative باشه
                src={recording.url?.startsWith("http") ? recording.url : `https://api.testplus.online${recording.url}`}
                onEnded={onAudioEnded}
                className="w-full"
                controls
              />

              {/* ✅ نمایش متن تبدیل‌شده */}
              <div className="text-sm text-gray-700 bg-gray-50 border rounded-lg p-3 text-start">
                <div className="text-xs text-gray-500 mb-1">
                  {t("speaking.transcript", "Transcript")}
                  {transcript?.confidence != null ? ` • ${(transcript.confidence * 100).toFixed(0)}%` : ""}
                </div>
                {transcript?.text ? (
                  <div dir={isRtl ? "rtl" : "ltr"}>{transcript.text}</div>
                ) : (
                  <div className="text-gray-400">
                    {t("speaking.no_transcript_yet", "No transcript available.")}
                  </div>
                )}
              </div>

              <div className="flex flex-wrap items-center justify-center gap-2">
                {playState === "playing" ? (
                  <button
                    onClick={pause}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border hover:bg-gray-50"
                  >
                    <Pause className="w-4 h-4" /> {t("speaking.pause", "Pause")}
                  </button>
                ) : (
                  <button
                    onClick={play}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border hover:bg-gray-50"
                  >
                    <Play className="w-4 h-4" /> {t("speaking.play", "Play")}
                  </button>
                )}

                <button
                  onClick={startPrep}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border hover:bg-gray-50"
                >
                  <RotateCcw className="w-4 h-4" /> {t("speaking.re_record", "Re-record")}
                </button>

                <button
                  onClick={removeRecording}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border hover:bg-gray-50"
                >
                  <Trash2 className="w-4 h-4" /> {t("common.remove", "Remove")}
                </button>

                <a
                  href={recording.url}
                  download={`speaking-${item?._id || "answer"}.webm`}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border hover:bg-gray-50"
                >
                  <Download className="w-4 h-4" /> {t("common.download", "Download")}
                </a>
              </div>

              <div className="text-xs text-gray-500">
                {t("speaking.length", {
                  defaultValue: "Length: {{s}}s",
                  s: Math.round((recording.durationMs || 0) / 1000),
                })}{" "}
                &middot; {recording.mimeType || "audio/webm"} &middot;{" "}
                {Math.ceil((recording.size || 0) / 1024)} KB
              </div>
            </div>
          )}

          {err ? (
            <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2 text-center">
              {err}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
